import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'

/** The owner's own moment: an anniversary, a menu launch, a local mela. */
export function AddMomentDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [starts, setStarts] = useState('')
  const [ends, setEnds] = useState('')
  const [lead, setLead] = useState('7')
  const [note, setNote] = useState('')

  const reset = () => {
    setTitle('')
    setStarts('')
    setEnds('')
    setLead('7')
    setNote('')
  }
  const create = useMutation({
    mutationFn: () =>
      apiClient.createCalendarMoment({ title: title.trim(), starts_on: starts, ends_on: ends || starts, lead_days: Number(lead), note: note.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'calendar'] })
      queryClient.invalidateQueries({ queryKey: ['marketing', 'home'] })
      toast({ title: 'Moment added', description: "You'll get a nudge before it." })
      reset()
      onOpenChange(false)
    },
    onError: (err) => toast({ title: 'Could not add the moment', description: describeApiError(err).message, variant: 'destructive' }),
  })
  const leadNum = Number(lead)
  const valid = title.trim() !== '' && starts !== '' && (!ends || ends >= starts) && Number.isInteger(leadNum) && leadNum >= 0 && leadNum <= 90

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a moment</DialogTitle>
          <DialogDescription>Your anniversary, a new menu launch, a local mela — anything worth a push.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-title">Title</Label>
            <Input id="new-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Our 3rd anniversary" maxLength={120} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-starts">Starts</Label>
              <Input id="new-starts" type="date" value={starts} onChange={(e) => setStarts(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-ends">Ends (optional)</Label>
              <Input id="new-ends" type="date" value={ends} min={starts} onChange={(e) => setEnds(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-lead">Remind me this many days before</Label>
            <Input id="new-lead" type="number" min={0} max={90} value={lead} onChange={(e) => setLead(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-note">Note (optional)</Label>
            <Textarea id="new-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            Add moment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
